import { queryOptions, useQuery } from '@tanstack/react-query';

import { SELECTION_LIMIT } from '@/constants';
import { getAccessToken } from '@/lib/tokenStorage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type SiteOption = {
  id: string;
  name: string;
  fertilizationSiteId?: string | null;
  fertilizationSite?: { id: string; name: string } | null;
};

type SitesPaginatedResponse = {
  items: SiteOption[];
  hasMore: boolean;
};

async function fetchSites(params: {
  search?: string;
  skip?: number;
  take?: number;
  id?: string;
  ids?: string[];
}): Promise<SitesPaginatedResponse> {
  const token = getAccessToken();
  const url = new URL('/api/v1/site', API_BASE_URL);

  if (params.search?.trim()) {
    url.searchParams.set('search', params.search.trim());
  }
  if (params.id) {
    url.searchParams.set('id', params.id);
  }
  if (params.ids?.length) {
    url.searchParams.set('id', `in:${params.ids.join(',')}`);
  }

  url.searchParams.set('order', 'name');

  if (params.skip != null) {
    url.searchParams.set('skip', String(params.skip));
  }
  if (params.take != null) {
    url.searchParams.set('take', String(params.take));
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch sites');
  }

  return (await response.json()) as SitesPaginatedResponse;
}

export function sitesQueryOptions(params: {
  search?: string;
  skip?: number;
  take?: number;
  enabled?: boolean;
}) {
  return queryOptions({
    queryKey: ['sites', params],
    enabled: params.enabled,
    async queryFn() {
      const result = await fetchSites(params);
      return result.items;
    },
  });
}

export function siteByIdQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['sites', 'byId', id],
    enabled: !!id,
    async queryFn() {
      const result = await fetchSites({ id, take: 1 });
      return result.items[0] ?? null;
    },
  });
}

export function useSitesByIdsQuery(ids: string[]) {
  return useQuery(sitesByIdsQueryOptions(ids));
}

export function sitesByIdsQueryOptions(ids: string[]) {
  return queryOptions({
    queryKey: ['sites', 'byIds', ids],
    enabled: !!ids.length,
    async queryFn() {
      const result = await fetchSites({ ids, take: SELECTION_LIMIT });
      return result.items;
    },
  });
}

export function useSiteByIdQuery(id: string | undefined) {
  return useQuery(siteByIdQueryOptions(id));
}

export async function fetchFertilizationSites(): Promise<SiteOption[]> {
  const token = getAccessToken();
  const url = new URL('/api/v1/site/fertilization', API_BASE_URL);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch fertilization sites');
  }

  return (await response.json()) as SiteOption[];
}

type AspirationSiteRestResponse = {
  id: string;
  name: string;
  fertilizationSiteId: string | null;
  fertilizationSiteName: string | null;
};

function normalizeAspirationSite(s: AspirationSiteRestResponse): SiteOption {
  return {
    id: s.id,
    name: s.name,
    fertilizationSiteId: s.fertilizationSiteId,
    fertilizationSite: s.fertilizationSiteId
      ? { id: s.fertilizationSiteId, name: s.fertilizationSiteName ?? '' }
      : null,
  };
}

async function fetchAspirationSites(path: string): Promise<SiteOption[]> {
  const token = getAccessToken();
  const url = new URL(path, API_BASE_URL);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch aspiration sites');
  }

  const data = (await response.json()) as AspirationSiteRestResponse[];
  return data.map(normalizeAspirationSite);
}

export type SiteFilter =
  | 'fertilization'
  | 'aspiration'
  | 'aspiration-locations'
  | 'search';

export type UseSitesParams = {
  filter: SiteFilter;
  search?: string;
  ids?: string[];
  enabled?: boolean;
};

async function fetchSitesByFilter(
  params: UseSitesParams,
): Promise<SiteOption[]> {
  switch (params.filter) {
    case 'fertilization':
      return fetchFertilizationSites();
    case 'aspiration':
      return fetchAspirationSites('/api/v1/site/aspiration');
    case 'aspiration-locations':
      return fetchAspirationSites('/api/v1/site/aspiration/locations');
    case 'search': {
      const ids = params.ids ?? [];
      const hasSearch = !!params.search?.trim();
      const [matches, selected] = await Promise.all([
        hasSearch
          ? fetchSites({ search: params.search, take: SELECTION_LIMIT })
          : Promise.resolve({ items: [] as SiteOption[], hasMore: false }),
        ids.length
          ? fetchSites({ ids, take: SELECTION_LIMIT })
          : Promise.resolve({ items: [] as SiteOption[], hasMore: false }),
      ]);
      const merged = new Map<string, SiteOption>();
      for (const s of [...selected.items, ...matches.items]) {
        merged.set(s.id, s);
      }
      return [...merged.values()];
    }
  }
}

export function useSites(params: UseSitesParams): {
  data: SiteOption[] | undefined;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['sites', params.filter, params.search ?? '', params.ids ?? []],
    queryFn: () => fetchSitesByFilter(params),
    enabled: params.enabled ?? true,
  });

  return {
    data: query.data,
    isLoading: query.isFetching,
  };
}
